import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles';
import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import Text, {
    TextDomTag,
} from 'client/widget-components/basicComponents/Text';
import TextArea, {
    TextAreaProps,
} from 'client/widget-components/basicComponents/TextArea';
import { textAreaDefaultStyles } from 'client/widget-components/components/TextField/styles';
import {
    mergeResponsiveStyles,
    nestResponsiveStyles,
    pickResponsiveStyles,
    ResponsiveStylesProp,
} from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { CSSProperties, useMemo } from 'react';
import styled from 'styled-components';
import { CUSTOMIZATIONS_DATA_GRABS } from '../../constants';

interface TextFieldCustomizationTextAreaProps
    extends Omit<TextAreaProps, 'styles'> {
    styles?: {
        textArea?: ResponsiveStylesProp;
        placeholder?: ResponsiveStylesProp;
    };
    error?: boolean;
}

// Placeholder should inherit global Paragraph styles `div.dmNewParagraph p`
const TextFieldCustomizationTextArea = (
    props: TextFieldCustomizationTextAreaProps
) => {
    const { styles, error, placeholder, value } = props;

    const { textareaStyles, placeholderStyles } = useMemo(() => {
        const _textareaStyles = mergeResponsiveStyles([
            textAreaDefaultStyles,
            error ? invalidTextareaStyles : {},
            nestResponsiveStyles(
                {
                    [RuntimeMediaQuery.COMMON]: {
                        opacity: 0,
                    },
                },
                {
                    innerSelector: '&::placeholder',
                }
            ),
            resizableTextAreaStyles,
            styles?.textArea,
        ]);

        return {
            textareaStyles: _textareaStyles,
            placeholderStyles: mergeResponsiveStyles([
                pickResponsiveStyles(_textareaStyles, propertiesSetToPick),
                hintDefaultStyles,
                styles?.placeholder,
            ]),
        };
    }, [styles?.textArea, styles?.placeholder, error]);

    return (
        <TextAreaWrapper className='dmNewParagraph'>
            <TextArea rows={1} autosize {...props} styles={textareaStyles} />
            {!value && (
                <Text
                    styles={placeholderStyles}
                    data-grab={
                        CUSTOMIZATIONS_DATA_GRABS.textAreaPlaceholderDataGrab
                    }
                    tag={TextDomTag.paragraph}
                >
                    {placeholder}
                </Text>
            )}
        </TextAreaWrapper>
    );
};

export default TextFieldCustomizationTextArea;

const TextAreaWrapper = styled.div`
    height: auto;
    min-height: 40px;
    position: relative;
    margin: 0;
    padding: 0;
`;

const invalidTextareaStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        borderColor: '#E33E3B',
    },
};

const commonPlaceholderStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    height: '40px',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    margin: 0,
    borderColor: 'transparent',
    overflow: 'hidden',
    color: 'rgba(201, 202, 205, 1)',
};

const resizableTextAreaStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        boxSizing: 'border-box',
        height: '40px',
    },
};

// Only set COMMON: it cascades to every breakpoint as a base (no-media) rule,
// so consumer `styles.placeholder` (also COMMON) can override it. Emitting the
// same values under per-breakpoint media queries produced higher-priority rules
// that blocked those overrides.
const hintDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: commonPlaceholderStyles,
};

const propertiesSetToPick: Set<keyof CSSProperties> = new Set([
    'border',
    'borderWidth',
    'borderTopWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderRightWidth',

    'borderRadius',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',

    'padding',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'paddingInline',
    'paddingBlock',
    'paddingInlineStart',
    'paddingInlineEnd',
    'paddingBlockStart',
    'paddingBlockEnd',
]);
